import { useEffect, useState } from 'react';
import { ActionError, useAction } from '../components/action.tsx';
import type { ConsoleAuth } from '../data/console-auth.ts';

const ERRORS: Readonly<Record<string, string>> = {
  expired: 'The sign-in took too long or was started in another tab. Try again.',
  failed: 'Signing in did not work. Try again, or use another account.',
};

export function SignInPage(props: {
  auth: ConsoleAuth;
  onSignedIn: () => void;
  hasInvite: boolean;
}) {
  const [providers, setProviders] = useState<Array<{ id: string; label: string }> | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [email, setEmail] = useState('dev@plato.example');
  const [staff, setStaff] = useState(true);
  const action = useAction();
  const reason = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('error');

  useEffect(() => {
    props.auth.listSignInProviders().then(setProviders, (cause: unknown) => {
      setFailure(cause instanceof Error ? cause.message : 'Could not reach the console API.');
    });
  }, [props.auth]);

  const external = providers?.filter((provider) => provider.id !== 'dev') ?? [];
  const dev = providers?.some((provider) => provider.id === 'dev') ?? false;

  return (
    <main className="sign-in" aria-labelledby="page-title">
      <h1 id="page-title">Sign in to the vendor console</h1>
      {props.hasInvite && (
        <p className="notice">Sign in with the account your invite was sent to, to accept it.</p>
      )}
      {reason !== null && (
        <p role="alert" className="notice notice-danger">
          {ERRORS[reason] ?? ERRORS.failed}
        </p>
      )}
      {failure !== null && (
        <p role="alert" className="notice notice-danger">
          {failure}
        </p>
      )}
      {providers === null && failure === null && <p role="status">Loading…</p>}
      <div className="stack">
        {external.map((provider) => (
          <a key={provider.id} className="button primary" href={props.auth.signInUrl(provider.id)}>
            Sign in with {provider.label}
          </a>
        ))}
      </div>
      {providers !== null && external.length === 0 && !dev && (
        <p>No sign-in is configured on this server.</p>
      )}
      {dev && (
        <form
          className="dev-sign-in"
          onSubmit={(event) => {
            event.preventDefault();
            void action
              .run(() => props.auth.devSignIn(email, staff))
              .then((ok) => {
                if (ok) props.onSignedIn();
              });
          }}
        >
          <h2>Development sign-in</h2>
          <p className="muted">This machine only. Any email, no password.</p>
          <label>
            Email
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={staff} onChange={(e) => setStaff(e.target.checked)} />
            Plato staff (can open every vendor)
          </label>
          <button type="submit" className="primary" disabled={action.pending}>
            Sign in
          </button>
          <ActionError error={action.error} />
        </form>
      )}
    </main>
  );
}
